import { DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { ConfigProvider, Pagination, Switch } from 'antd';
import React, { useMemo } from 'react';
import './AutomatedCampaignTable.scss'

import viVN from 'antd/locale/vi_VN';
import { CustomLoadingOverlayTable, dataPagination, formatPrice, getUserPermissions, getWidthFromHeader } from '../../../until';
import { useSelector } from 'react-redux';
import { selectorPermission } from '../../../store/slices/permissionSlice';
import { formatNumber } from '../../../services/mixin';
import { useNavigate } from 'react-router-dom';
import { AllCommunityModule, ColDef, ModuleRegistry } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { NodataTable } from '../../../components/NoDataTable';
import { NO_DATA } from '../../../env';
import useThrottle from '../../../hook/useThrottle';
import { activeCampaign } from '../../../api/campaign/campaignApi';
import useToast from '../../../hook/useToast';
import { StatusSendCampaign } from '../../../services/data';

ModuleRegistry.registerModules([AllCommunityModule]);

interface Props {
  tableData: Array<any>
  pagination: { current: number, pageSize: number, total: number }
  loading?: boolean
  isLoading?: boolean
  columnDefs?: ColDef[]
  onShowDetail?: () => void
  gListCampaignAuto: (pageCurent: number, pageSize: number) => void

  onPaginationChange: (page: number, pageSize: number) => void;
  openModalDelete: (id: number) => void
}

const currencyFormat = (number: any) => {
  try {
    number = parseInt(number);
    const formattedNumber = new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(number);
    return formattedNumber;
  } catch (error) {
    return number;
  }
};

const numberFormat = (number: any) => {
  if (typeof number === 'number') {
    return new Intl.NumberFormat().format(number)
  }
  return number
}

const AutomatedCampaignTable: React.FC<Props> = (props) => {
  const { tableData, pagination, onPaginationChange, openModalDelete, columnDefs: columnDefsProp, loading = false, gListCampaignAuto } = props

  const permissionSlice = useSelector(selectorPermission)
  const { delete: isDelete } = getUserPermissions(permissionSlice, location.pathname)
  const navigate = useNavigate()
  const pushToast = useToast();

  const onChangeActive = useThrottle(async (value: number) => {
    const result = await activeCampaign(value)
    if (result?.status) {
      gListCampaignAuto(pagination.current, pagination.pageSize)
    } else {
      pushToast(result?.message, "warning")
    }
  }, 2000)

  const internalColumnDefs = useMemo<ColDef[]>(() => [
    {
      headerName: 'Hành động',
      field: 'id',
      colId: 'action',
      width: 200,
      sortable: false,
      cellRenderer: (params: any) => {
        return (
          <div className="d-flex align-center h-full gap-2">
            <EyeOutlined
              className={`text-sm border-D9D9D9 p-5-px bg-white rounded-2 cursor-pointer `}
              onClick={() => navigate(
                '/marketing/campaign-sends-notifications/access',
                {
                  state: {
                    id: params.data.id,
                    action: 'access',
                    isAutoCampaign: false,
                    view: params.data.view
                  }
                }
              )}
            />
            {params.data.role !== 0 ?
              <EditOutlined
                className={`text-sm border-D9D9D9 p-5-px bg-white rounded-2 cursor-pointer `}
                onClick={() => navigate(
                  '/marketing/campaign-sends-notifications/edit',
                  { state: { id: params.data.id, action: 'edit', isAutoCampaign: false } }
                )}
              />
              : ''
            }
            <DeleteOutlined
              className={`text-F5222D text-sm border-EAECF0 p-5-px bg-white rounded-2 cursor-pointer ${!isDelete && "disabled"}`}
              onClick={() => openModalDelete(Number(params.data.id))}
            />
            <Switch
              className="rs-switch rs-switch--sm"
              checked={params.data.view ? true : false}
              onChange={() => onChangeActive(params.data.id)}
            />
          </div>
        )
      },

      resizable: false,
      suppressMovable: true,
      lockPosition: 'left',
    },
    {
      headerName: 'Mục tiêu',
      field: 'muc_tieu',
      colId: 'muc_tieu',
      width: getWidthFromHeader('Mục tiêu'),
      cellRenderer: (params: any) => <div className='text-2-row'>{params.value}</div>,
    },
    {
      headerName: 'Mã chiến dịch',
      field: 'ma_chien_dich',
      colId: 'ma_chien_dich',
      width: getWidthFromHeader('Mã chiến dịch'),
      cellRenderer: (params: any) => <div className='text-2-row'>{params.value}</div>,
    },
    {
      headerName: 'Tên chiến dịch',
      field: 'ten_chien_dich',
      colId: 'ten_chien_dich',
      width: getWidthFromHeader('Tên chiến dịch'),
      cellRenderer: (params: any) => <div className='text-2-row'>{params.value}</div>,
    },
    {
      headerName: 'Trạng thái',
      field: 'trang_thai',
      colId: 'trang_thai',
      width: getWidthFromHeader('Trạng thái'),
      cellRenderer: (params: any) => (
        <div className={`status ${params.value === 2 ? 'status-sent' : params.value === 1 ? 'status-sending' : 'status-pending'}`}>
          <span>{StatusSendCampaign[params.value]?.label}</span>
        </div>
      ),
    },
    {
      headerName: 'Kênh gửi',
      field: 'kenh_gui',
      colId: 'kenh_gui',
      width: getWidthFromHeader('Kênh gửi'),
      sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (params: any) => (
        <div className="d-flex align-center h-full gap-1">
          {params.value?.map((item: any) => (
            <img src={item.img} alt={item.text} className="channel-img" key={item.text} title={item.text} />
          ))}
        </div>
      ),
    },
    // {
    //   headerName: 'Khách hàng nhận',
    //   field: 'nhom_khach_hang_nhan',
    //   colId: 'nhom_khach_hang_nhan',
    //   width: getWidthFromHeader('Khách hàng nhận'),
    //   sortable: false,
    //   cellRenderer: (params: any) => (
    //     <div
    //       onClick={() => navigate('/marketing/campaign-sends-notifications/access', { state: { id: params.data.id, action: 'access', isAutoCampaign: params.data.auto === 1 ? true : false } })}
    //       className="d-flex justify-between h-full align-center cursor-pointer"
    //     >
    //       <span>{formatPrice(params.value)}</span>
    //       < span className="primary-7" > Xem DS</span >
    //     </div >
    //   ),
    // },
    {
      headerName: 'Đã gửi',
      field: 'da_gui',
      colId: 'da_gui',
      width: getWidthFromHeader('Đã gửi'),
      cellStyle: { textAlign: 'right' },
      cellRenderer: (params: any) => (
        <>
          <span>{numberFormat(params.value)}</span>
          <span className="neutral-gray-400">{formatPrice(params.data.total)}</span>
        </>
      ),
    },
    {
      headerName: 'Thời gian gửi',
      field: 'thoi_gian_gui',
      colId: 'thoi_gian_gui',
      width: getWidthFromHeader('Thời gian gửi'),
    },
    {
      headerName: 'Đã chi tiêu',
      field: 'da_chi_tieu',
      colId: 'da_chi_tieu',
      width: getWidthFromHeader('Đã chi tiêu'),
      cellRenderer: (params: any) => <span>{currencyFormat(params.value)}</span>,
    },
    {
      headerName: 'Người tạo',
      field: 'nguoi_tao',
      colId: 'nguoi_tao',
      width: getWidthFromHeader('Người tạo'),
      cellRenderer: (params: any) => <span>{params.value || NO_DATA}</span>,
    },
    {
      headerName: 'Ngày tạo',
      field: 'ngay_tao',
      colId: 'ngay_tao',
      width: getWidthFromHeader('Ngày tạo'),
      cellRenderer: (params: any) => <span>{params.value || NO_DATA}</span>,
    },
  ], [isDelete, navigate, onChangeActive, openModalDelete])

  const columnAutoDefs = useMemo(() => {
    if (!columnDefsProp) return internalColumnDefs;
    return columnDefsProp.map(col => {
      const match = internalColumnDefs.find(c => c.colId === col.colId);
      return { ...match, ...col };
    });
  }, [columnDefsProp, internalColumnDefs]);

  return (
    <div className="custom-page-css flex flex-col flex-1 justify-between">
      <div className="ag-theme-alpine empty-test-td custom-table-ag-v2" style={{ width: '100%' }}>
        <AgGridReact
          domLayout="autoHeight"
          rowData={tableData}
          columnDefs={columnAutoDefs}
          defaultColDef={{ resizable: true, sortable: true }}
          loading={loading}
          loadingOverlayComponent={CustomLoadingOverlayTable}
          rowHeight={55}
          suppressDragLeaveHidesColumns={true}
          overlayNoRowsTemplate={NodataTable}
          getRowStyle={(params: any) => ({
            backgroundColor: params?.node?.rowIndex % 2 === 0 ? '#ffffff' : '#f7f9fc',
          })}
        />
      </div>

      <ConfigProvider locale={viVN}>
        <div className="pagination-block">
          <Pagination
            showSizeChanger
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            itemRender={(page, type, originalElement) => {
              if (type === 'page') return <span>{formatNumber(page)}</span>;
              return originalElement;
            }}
            showTotal={() => `Tổng cộng ${dataPagination(pagination.total, pagination.pageSize, pagination.current).totalPage} trang`}
            className='rs-pagination'
            onChange={onPaginationChange}
          />
        </div>
      </ConfigProvider>
    </div>
  )
}

export default AutomatedCampaignTable
